import React, {useEffect, useState, useContext} from "react";
import {useTranslation} from "react-i18next";
import {useFetch} from "../../../hooks/useFetch";
import {useStore} from "../../../hooks/useStore";
import {arrayAlphabeticalSort} from "../../../utils/helpers";
import {SelectField} from "./GlobalSubCompanyInput.styles";
import turkishToEnglish from "../../../constants/turkishToEnglish";

const GlobalSubCompanyInput = ({
    value,
    onChange,
    onSelectedSubCompaniesChange = () => {},
    onFilteredCountChange = () => {},
    company,
    companies,
    disabled = false,
    initialCall = false,
    mode = undefined,
    showAllOption = false,
    allowClear = false,
    loadWithDelay = 0,
    city = undefined,
    optionFilter = () => {
        return true;
    },
    size = "middle",
    onSubCompanySelect = () => {}, // Yeni callback
    hideSuffixIcon = false,
    ...props
}) => {
    const {t} = useTranslation();
    const stores = useStore();

    const [delayLoading, setDelayLoading] = useState(false);
    const [subCompaniesLoading, subCompanies, setSubCompanies, setSubCompaniesLoading] = useFetch({
        defaultDataValue: [],
        fetchFn: stores.companyStore.getSubCompanyList.bind(null, {
            companyId: company,
            companies: companies?.join(".") || "",
            city: city,
            limit: 5000
        }),
        dependencies: [company, companies, city],
        initialCall: initialCall,
        condition: company && company !== "pool",
        callbackFunc: async response => {
            // Otomatik seçim yapma, sadece loading state'ini yönet
            if (response?.rows?.length > 0) {
                setDelayLoading(true);
                await new Promise(resolve => setTimeout(resolve, loadWithDelay));
                setDelayLoading(false);
            }
        }
    });

    useEffect(() => {
        if (subCompanies?.rows) {
            const filteredSubCompanies = subCompanies.rows.filter(optionFilter);
            onFilteredCountChange(filteredSubCompanies.length);
        }
    }, [subCompanies, optionFilter, onFilteredCountChange]);

    const normalizeText = text => {
        if (!text) return "";

        let normalized = text.toLowerCase();

        normalized = turkishToEnglish(normalized);

        normalized = normalized.normalize("NFD").replace(/[\u0300-\u036f]/g, "");

        return normalized;
    };

    const onSelect = React.useCallback(
        value => {
            onChange(value);
            onSelectedSubCompaniesChange(
                subCompanies?.rows?.filter(subCompany => value?.includes(subCompany._id))
            );

            // Seçilen alt firmayı callback ile bildir
            const selectedSubCompany = subCompanies?.rows?.find(
                subCompany => subCompany._id === value
            );
            if (selectedSubCompany) {
                onSubCompanySelect(selectedSubCompany);
            }
        },
        [onChange, onSelectedSubCompaniesChange, onSubCompanySelect, subCompanies?.rows]
    );

    // Seçili alt firma ismini bul
    const selectedSubCompanyName = React.useMemo(() => {
        if (!value || !subCompanies?.rows) return null;
        const selected = subCompanies.rows.find(subCompany => subCompany._id === value);
        return selected?.name || null;
    }, [value, subCompanies?.rows]);

    // Loading durumunda placeholder göster
    const displayValue = React.useMemo(() => {
        if (subCompaniesLoading || delayLoading) {
            return undefined; // Loading durumunda value gösterme, placeholder göster
        }
        return value;
    }, [value, subCompaniesLoading, delayLoading]);

    return (
        <React.Fragment>
            {/* <h5>{t("fields.subcompany", {ns: "common"})}</h5> */}
            <SelectField
                loading={subCompaniesLoading || delayLoading}
                data-testid="globalSubCompanySelect"
                value={displayValue}
                onChange={onSelect}
                allowClear={allowClear}
                placeholder={
                    subCompaniesLoading || delayLoading
                        ? "Yükleniyor..."
                        : t("fields.subcompany", {ns: "common"})
                }
                disabled={
                    hideSuffixIcon ||
                    (disabled &&
                        (!company ||
                            company === "all" ||
                            company === "pool" ||
                            subCompaniesLoading))
                }
                mode={mode}
                size={size}
                suffixIcon={hideSuffixIcon ? null : undefined}
                filterOption={(input, option) =>
                    normalizeText(option.children).indexOf(normalizeText(input)) >= 0 ||
                    normalizeText(option.value).indexOf(normalizeText(input)) >= 0
                }
                {...props}
            >
                {showAllOption && (
                    <SelectField.Option value="all" data-testid="option">
                        {t("filters.all", {ns: "common"})}
                    </SelectField.Option>
                )}
                {subCompanies?.rows &&
                    subCompanies?.rows.length > 0 &&
                    arrayAlphabeticalSort([...subCompanies?.rows], "name")
                        .filter(optionFilter)
                        .map(subCompany => (
                            <SelectField.Option
                                key={subCompany._id}
                                value={subCompany._id}
                                data-testid="globalSubCompanySelect_option"
                            >
                                {subCompany.name}
                            </SelectField.Option>
                        ))}
            </SelectField>
        </React.Fragment>
    );
};

export default GlobalSubCompanyInput;
